/* eslint-disable react/display-name */
import { useContext, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import HomePage from './pages';
import LoginPage from './pages/login';
import ThankYouPage from './pages/ThankYou';
import Error404Page from './pages/404';
import Header from './components/page/Header';
import { Container } from 'react-bootstrap';
import CreatePage from './pages/create';
import Footer from 'src/components/page/Footer';
import UsersPage from './pages/users';
import Page from './components/page/Page';
import AuthContext from './contexts/auth-context';
import ToastContext from './contexts/toast-context';
import Submissions from 'src/components/submissions';
import FormPage from './pages/Form';
import ErrorPage from 'src/pages/Error';

const makePage = (title, Component) => (props) =>
  (
    <Page title={title}>
      <Component {...props} />
    </Page>
  );

const makeRedirect = (location) => () => <Redirect to={location} />;

// Usage is the same as the regular Route component, though also adds a condition that
// indicates if access to the route is permitted or not. If the condition is a function, it will be executed
// and it's return value will be used. Otherwise, it will be coerced to a boolean and evaluated. Also adds
// the deniedComponent and deniedRender props that are triggered when the page is restricted.
// deniedComponent and deniedRender props function in a manner similar to component and
// render props in the standard Route component.
const RestrictedRoute = ({
  component: Component,
  deniedComponent: DeniedComponent,
  render,
  deniedRender,
  condition,
  ...props
}) => {
  const routeAllowed = typeof condition === 'function' ? condition() : !!condition;

  return (
    <Route
      {...props}
      render={(routeProps) => {
        if (routeAllowed) {
          return Component ? <Component {...routeProps} /> : render(routeProps);
        } else {
          return DeniedComponent ? <DeniedComponent {...routeProps} /> : deniedRender(routeProps);
        }
      }}
    />
  );
};

/**
 * Creates a route component that permits navigation only if the user role passes the checks
 * applied in the `isUserAuthorized` callback function supplied by the caller.
 *
 * `isUserAuthorized` is passed an object containing role and login information from the auth
 * context of the application. This callback should return a boolean indicating whether the
 * combination of roles/login state is valid to grant access to the page. If the callback returns
 * `true` the page will load as normal. If the callback returns false, an access denied toast will
 * be displayed instead and the user will be redirected to the app home page.
 *
 * @param {({
 *  isLoggedIn: boolean,
 *  isAdmin: boolean,
 *  user: any
 * }) => boolean } isUserAuthorized
 * @returns {import('react').FunctionComponent} The resulting route component.
 */
const makeRoleRequiredRoute = (isUserAuthorized) => {
  return (props) => {
    const auth = useContext(AuthContext);
    const location = useLocation();
    const { error } = useContext(ToastContext);

    const isAuthorized = isUserAuthorized(auth);

    useEffect(() => {
      if (!auth.logoutNotified && !isAuthorized) {
        error(
          auth.isLoggedIn
            ? 'Access Denied! You do not have sufficient privileges to access the page.'
            : 'Please log in to view this page.',
          {
            toastId: 'pageAccessToast',
          }
        );
      }
    });

    return (
      <RestrictedRoute
        {...props}
        condition={isAuthorized}
        deniedComponent={makeRedirect(
          !auth.isLoggedIn ? `/login?redirect=${location.pathname}` : '/'
        )}
      />
    );
  };
};

const LoginRequiredRoute = (props) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const { error } = useContext(ToastContext);

  useEffect(() => {
    if (!auth.logoutNotified && !auth.isLoggedIn) {
      error('Please log in to view this page.', {
        toastId: 'pageAccessToast',
      });
    }
  });

  return (
    <RestrictedRoute
      {...props}
      condition={auth.isLoggedIn}
      deniedComponent={makeRedirect(`/login?redirect=${location.pathname}`)}
    />
  );
};

function App() {
  const AdminOnlyRoute = makeRoleRequiredRoute((auth) => auth.isAdmin);

  return (
    <div className="App">
      <Header />
      <Container>
        <Switch>
          <Route exact path="/login" component={makePage('Login', LoginPage)} />
          <Route path="/thankyou" component={makePage('Thank You', ThankYouPage)} />
          <Route path="/error" component={makePage('Error', ErrorPage)} />
          <Route path="/report" component={makePage('Submit a Tip', FormPage)} />
          <LoginRequiredRoute
            exact
            path="/create"
            component={makePage('Create sale', CreatePage)}
          />
          <LoginRequiredRoute
            exact
            path="/submissions/"
            component={makePage('Submissions', Submissions)}
          />
          <AdminOnlyRoute exact path="/users" component={makePage('Users', UsersPage)} />
          <Route exact path={['/', '/home']} component={makePage('Home', HomePage)} />
          <Route path="*" component={makePage('Page Not Found', Error404Page)} />
        </Switch>
      </Container>
      <Footer />
    </div>
  );
}

export default App;
